import React from "react";
import GoogleMapReact from "google-map-react";
import MapMarker from "./MapMarker";
import RestaurantMarker from "./RestaurantMarker";

/**
 * Google Map Container Component
 */
const GoogleMapContainer = ({
  GOOGLE_MAPS_API_KEY,
  defaultCenter,
  defaultZoom,
  schedules,
  onGoogleApiLoaded,
  onError,
  filteredSchedules,
  handleMarkerClick,
  getStatusColor,
  getStatusText,
  restaurantLatitude,
  restaurantLongitude,
  t,
  loading,
  children,
}) => {

  return (
    <div className="relative">
      <div className="w-full h-96" style={{ minHeight: "400px" }}>
        <GoogleMapReact
          bootstrapURLKeys={{
            key: GOOGLE_MAPS_API_KEY,
            libraries: ["places"],
          }}
          defaultCenter={defaultCenter}
          defaultZoom={defaultZoom}
          center={schedules.length > 0 ? undefined : defaultCenter}
          zoom={schedules.length > 0 ? undefined : defaultZoom}
          options={{
            styles: [
              {
                featureType: "poi",
                elementType: "labels",
                stylers: [{ visibility: "off" }],
              },
            ],
          }}
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={onGoogleApiLoaded}
          onError={onError}
        >
          {/* Render delivery markers */}
          {filteredSchedules.map((schedule, index) => {
            if (!schedule.location?.latitude || !schedule.location?.longitude) {
              return null;
            }

            const lat = parseFloat(schedule.location.latitude);
            const lng = parseFloat(schedule.location.longitude);

            // Validate coordinates to prevent the error
            if (isNaN(lat) || isNaN(lng)) {
              console.warn(`Invalid coordinates for schedule ${schedule.id}:`, {
                lat: schedule.location.latitude,
                lng: schedule.location.longitude,
              });
              return null;
            }

            return (
              <MapMarker
                key={`delivery-${schedule.id}-${index}`}
                lat={lat}
                lng={lng}
                schedule={schedule}
                getStatusColor={getStatusColor}
                getStatusText={getStatusText}
                onClick={handleMarkerClick}
              />
            );
          })}

          {/* Render restaurant marker */}
          {restaurantLatitude &&
            restaurantLongitude &&
            (() => {
              const lat = parseFloat(restaurantLatitude);
              const lng = parseFloat(restaurantLongitude);

              // Validate restaurant coordinates
              if (!isNaN(lat) && !isNaN(lng)) {
                return (
                  <RestaurantMarker
                    key="restaurant-marker"
                    lat={lat}
                    lng={lng}
                    t={t}
                  />
                );
              }
              return null;
            })()}
        </GoogleMapReact>
      </div>

      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
          <div className="flex items-center space-x-2">
            <svg
              className="animate-spin h-5 w-5 text-gray-500"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span className="text-sm text-gray-500">
              {t("scheduleMap.loading")}
            </span>
          </div>
        </div>
      )}

      {/* No schedules overlay */}
      {!loading && schedules.length === 0 && (
        <div className="absolute inset-0 bg-gray-50 bg-opacity-90 flex items-center justify-center">
          <div className="text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {t("scheduleMap.noSchedules")}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {t("scheduleMap.noSchedulesMessage")}
            </p>
          </div>
        </div>
      )}

      {children}
    </div>
  );
};

export default GoogleMapContainer;
